import { AddCircleOutline, DeleteOutline, DragIndicator } from '@mui/icons-material';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Editor číselníku stupňů projektové dokumentace (šablona tenantu). Odpovídá editoru
 * ve validátoru (DocumentationStagesEditor) - název je identita stupně.
 */

/** Řádek editovaného číselníku. Id 0 = nový, ještě neuložený stupeň. */
export interface EditableDocumentationStage {
    /** Stabilní klíč řádku - id se u nových stupňů teprve přidělí. */
    key: string;
    id: number;
    name: string;
}

export const DOCUMENTATION_STAGE_NAME_MAX_LENGTH = 100;

/** Vlastní typ přetahovaných dat - obyčejný text by šel pustit do pole s názvem stupně. */
const DRAG_DATA_TYPE = 'application/x-documentation-stage';

export function normalizeStageName(name: string | undefined | null): string {
    return (name ?? '').trim();
}

/** Názvy se porovnávají bez ohledu na velikost písmen - stejně jako na serveru. */
export function stageNamesEqual(a: string | undefined | null, b: string | undefined | null): boolean {
    return normalizeStageName(a).toLocaleLowerCase() === normalizeStageName(b).toLocaleLowerCase();
}

let newStageKeyCounter = 0;
export function createNewStageKey(): string {
    newStageKeyCounter++;
    return `new-${newStageKeyCounter}`;
}

/** Chyba názvu řádku (prázdný, příliš dlouhý, duplicitní); null = v pořádku. */
export function getStageNameError(stage: EditableDocumentationStage, stages: EditableDocumentationStage[], t: (key: string) => string): string | null {
    const name = normalizeStageName(stage.name);

    if (name === '')
        return t('DocumentationStages.ErrorEmptyNameShort');

    if (name.length > DOCUMENTATION_STAGE_NAME_MAX_LENGTH)
        return t('DocumentationStages.ErrorNameTooLongShort');

    if (stages.some(s => s.key !== stage.key && stageNamesEqual(s.name, name)))
        return t('DocumentationStages.ErrorDuplicateNameShort');

    return null;
}

export function hasStageNameErrors(stages: EditableDocumentationStage[], t: (key: string) => string): boolean {
    return stages.some(s => getStageNameError(s, stages, t) !== null);
}

interface DocumentationStagesEditorProps {
    stages: EditableDocumentationStage[];
    onChange: (stages: EditableDocumentationStage[]) => void;
    disabled?: boolean;
}

export default function DocumentationStagesEditor(props: Readonly<DocumentationStagesEditorProps>) {
    const { stages, onChange, disabled } = props;
    const { t } = useTranslation();
    const [newName, setNewName] = useState<string>('');
    /** Řádek se stisknutým úchytem. Přetahovat jde jen za úchyt, aby šlo v názvu dál označovat text myší. */
    const [grabbedKey, setGrabbedKey] = useState<string | null>(null);
    const [dragKey, setDragKey] = useState<string | null>(null);
    /** Mezera, do které se stupeň pustí: 0 = před první řádek, stages.length = za poslední. */
    const [dropIndex, setDropIndex] = useState<number | null>(null);

    const newNameTrimmed = normalizeStageName(newName);
    const newNameDuplicate = newNameTrimmed !== '' && stages.some(s => stageNamesEqual(s.name, newNameTrimmed));
    const canAdd = !disabled && newNameTrimmed !== '' && !newNameDuplicate && newNameTrimmed.length <= DOCUMENTATION_STAGE_NAME_MAX_LENGTH;
    const dragIndex = dragKey === null ? -1 : stages.findIndex(s => s.key === dragKey);

    const addStage = () => {
        if (!canAdd)
            return;

        onChange([...stages, { key: createNewStageKey(), id: 0, name: newNameTrimmed }]);
        setNewName('');
    };

    const rename = (key: string, name: string) => {
        onChange(stages.map(s => s.key === key ? { ...s, name: name } : s));
    };

    /** Podle poloviny řádku se stupeň pustí nad něj, nebo pod něj. */
    const updateDropIndex = (index: number, e: React.DragEvent<HTMLDivElement>) => {
        if (dragIndex < 0)
            return;

        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const rect = e.currentTarget.getBoundingClientRect();
        setDropIndex(index + (e.clientY - rect.top > rect.height / 2 ? 1 : 0));
    };

    const commitDrop = () => {
        // index se po odebrání přetahovaného stupně posune, pokud šel dolů
        if (dragIndex >= 0 && dropIndex !== null && dropIndex !== dragIndex && dropIndex !== dragIndex + 1) {
            const target = dropIndex > dragIndex ? dropIndex - 1 : dropIndex;
            const reordered = [...stages];
            const [moved] = reordered.splice(dragIndex, 1);
            reordered.splice(target, 0, moved);
            onChange(reordered);
        }

        endDrag();
    };

    const endDrag = () => {
        setGrabbedKey(null);
        setDragKey(null);
        setDropIndex(null);
    };

    const remove = (key: string) => {
        onChange(stages.filter(s => s.key !== key));
    };

    /** Čára v mezeře mezi řádky; drží si místo i když není vidět, aby seznam při tažení neposkakoval. */
    const dropLine = (index: number) => {
        // pozice, kde by stupeň zůstal na svém místě, se nezvýrazňuje
        const active = dropIndex === index && dragIndex >= 0 && index !== dragIndex && index !== dragIndex + 1;

        return (
            <Box sx={{
                height: '2px',
                margin: '2px 0',
                borderRadius: '1px',
                bgcolor: active ? 'primary.main' : 'transparent',
                transition: 'background-color 120ms',
            }} />
        );
    };

    return (
        <Box
            sx={{ display: 'flex', flexDirection: 'column' }}
            onDragOver={(e) => { if (dragIndex >= 0) e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); commitDrop(); }}
        >
            {stages.length === 0 && <Typography sx={{ margin: '10px', fontSize: '13px', color: 'text.secondary' }}>{t('DocumentationStages.Empty')}</Typography>}
            {stages.map((stage, index) => {
                const nameError = getStageNameError(stage, stages, t);
                const isDragging = dragKey === stage.key;

                return (
                    <React.Fragment key={stage.key}>
                        {dropLine(index)}
                        <Box
                            draggable={!disabled && grabbedKey === stage.key}
                            onDragStart={(e) => {
                                setDragKey(stage.key);
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData(DRAG_DATA_TYPE, stage.key);
                            }}
                            onDragOver={(e) => updateDropIndex(index, e)}
                            onDrop={(e) => { e.preventDefault(); commitDrop(); }}
                            onDragEnd={endDrag}
                            sx={{ opacity: isDragging ? 0.5 : 1 }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: 1,
                                borderColor: nameError !== null ? 'error.main' : 'divider',
                                borderRadius: 1,
                                paddingRight: '4px',
                                bgcolor: 'background.paper',
                                '&:hover': { borderColor: nameError !== null ? 'error.main' : 'text.primary' },
                            }}>
                                <Box
                                    onMouseDown={() => { if (!disabled) setGrabbedKey(stage.key); }}
                                    onMouseUp={() => { if (!dragKey) setGrabbedKey(null); }}
                                    sx={{ cursor: disabled ? 'default' : 'grab', color: 'text.disabled', display: 'flex', alignItems: 'center', paddingLeft: '4px' }}
                                >
                                    <DragIndicator fontSize='small' />
                                </Box>
                                <TextField
                                    size='small'
                                    sx={{ flexGrow: 1, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                                    placeholder={t('DocumentationStages.Name')}
                                    inputProps={{ 'aria-label': t('DocumentationStages.Name') }}
                                    value={stage.name}
                                    disabled={disabled}
                                    error={nameError !== null}
                                    onChange={(e) => rename(stage.key, e.currentTarget.value)} />
                                <Tooltip title={t('DocumentationStages.Delete')}>
                                    <span>
                                        <IconButton size='small' disabled={disabled} onClick={() => remove(stage.key)}>
                                            <DeleteOutline fontSize='small' />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            {nameError !== null && <Typography sx={{ fontSize: '12px', color: 'error.main', marginLeft: '14px', marginTop: '3px' }}>{nameError}</Typography>}
                        </Box>
                    </React.Fragment>
                );
            })}
            {stages.length > 0 && dropLine(stages.length)}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '16px' }}>
                <TextField
                    size='small'
                    sx={{ flexGrow: 1 }}
                    label={t('DocumentationStages.NewStage')}
                    value={newName}
                    disabled={disabled}
                    error={newNameDuplicate}
                    helperText={newNameDuplicate ? t('DocumentationStages.ErrorDuplicateNameShort') : undefined}
                    onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }}
                    onChange={(e) => setNewName(e.currentTarget.value)} />
                <Tooltip title={t('DocumentationStages.Add')}>
                    <span>
                        <IconButton color='primary' disabled={!canAdd} onClick={addStage}>
                            <AddCircleOutline />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
}
